/* ============================================================
   GEBERT & WESCHKA — Bauteil-Vorschau
   Rendert eine hochgeladene STL (clientseitig) oder eine
   serverseitig tessellierte STEP-Mesh (URL) live im Browser.
   Nutzt window.THREE (self-hosted, wie gw-3d.js). Custom Orbit.
   API:  GWPartViewer.render(hostEl, { arrayBuffer })  // STL-Upload
         GWPartViewer.render(hostEl, { url })          // STEP-Mesh
   ============================================================ */
(function () {
  'use strict';

  var THREE_URL = 'https://unpkg.com/three@0.160.0/build/three.module.js';
  var threeP = null;
  function injectThree(url, onerror) {
    var s = document.createElement('script');
    s.type = 'module';
    s.textContent = 'import * as T from "' + url + '"; window.THREE = T; window.dispatchEvent(new Event("gw-three-ready"));';
    s.onerror = onerror;
    (document.head || document.documentElement).appendChild(s);
  }
  function loadThree() {
    if (window.THREE && window.THREE.WebGLRenderer) return Promise.resolve(window.THREE);
    if (!threeP) threeP = new Promise(function (res, rej) {
      var settled = false;
      function ok() { if (!settled && window.THREE) { settled = true; res(window.THREE); } }
      window.addEventListener('gw-three-ready', ok, { once: true });
      // nur die Kopie aus dem Theme — kein Nachladen von einem fremden Server ohne Einwilligung
      var primary = window.GW_THREE_URL || THREE_URL;
      injectThree(primary, function () { if (!settled) { settled = true; rej(new Error('three load failed')); } });
      setTimeout(function () { if (!settled) { if (window.THREE) ok(); else { settled = true; rej(new Error('three timeout')); } } }, 15000);
    });
    return threeP;
  }

  /* ---------- STL parser (binär + ASCII) → BufferGeometry ---------- */
  function looksBinary(buffer) {
    if (buffer.byteLength < 84) return false;
    var n = new DataView(buffer).getUint32(80, true);
    return 84 + n * 50 === buffer.byteLength;
  }
  function parseBinary(THREE, buffer) {
    var dv = new DataView(buffer), n = dv.getUint32(80, true);
    var pos = new Float32Array(n * 9), nor = new Float32Array(n * 9), off = 84, anyN = false;
    for (var i = 0; i < n; i++) {
      var nx = dv.getFloat32(off, true), ny = dv.getFloat32(off + 4, true), nz = dv.getFloat32(off + 8, true);
      if (nx || ny || nz) anyN = true;
      for (var v = 0; v < 3; v++) {
        var b = off + 12 + v * 12, idx = i * 9 + v * 3;
        pos[idx] = dv.getFloat32(b, true); pos[idx + 1] = dv.getFloat32(b + 4, true); pos[idx + 2] = dv.getFloat32(b + 8, true);
        nor[idx] = nx; nor[idx + 1] = ny; nor[idx + 2] = nz;
      }
      off += 50;
    }
    var g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    if (anyN) g.setAttribute('normal', new THREE.BufferAttribute(nor, 3)); else g.computeVertexNormals();
    return g;
  }
  function parseASCII(THREE, text) {
    var verts = [], re = /vertex\s+([\-0-9.eE+]+)\s+([\-0-9.eE+]+)\s+([\-0-9.eE+]+)/g, m;
    while ((m = re.exec(text))) verts.push(+m[1], +m[2], +m[3]);
    var g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(verts, 3));
    g.computeVertexNormals();
    return g;
  }
  function parseSTL(THREE, buffer) {
    if (looksBinary(buffer)) return parseBinary(THREE, buffer);
    return parseASCII(THREE, new TextDecoder().decode(buffer));
  }

  /* ---------- Viewer ---------- */
  function Viewer(THREE, host, geo) {
    this.T = THREE; this.host = host; this.raf = 0; this.dragging = false; this.idle = 0; this.lastT = 0;
    var renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping; renderer.toneMappingExposure = 1.15;
    this.renderer = renderer; host.appendChild(renderer.domElement);

    var scene = new THREE.Scene(); this.scene = scene;
    this._env(THREE, renderer, scene);
    scene.add(new THREE.HemisphereLight(0xeaf1ff, 0x39414e, 1.05));
    var key = new THREE.DirectionalLight(0xffffff, 1.5); key.position.set(4, 7, 5); scene.add(key);
    var rim = new THREE.DirectionalLight(0x9fb6ff, 0.4); rim.position.set(-6, 3, -5); scene.add(rim);

    // center + scale to a comfortable unit size
    geo.computeBoundingBox();
    var c = new THREE.Vector3(); geo.boundingBox.getCenter(c);
    geo.translate(-c.x, -c.y, -c.z);
    geo.computeBoundingSphere();
    var r = geo.boundingSphere.radius || 1;
    var mat = new THREE.MeshStandardMaterial({ color: 0xb9c1ca, metalness: 0.86, roughness: 0.36, envMapIntensity: 0.8 });
    var mesh = new THREE.Mesh(geo, mat); scene.add(mesh);
    this.mesh = mesh; this.mat = mat; this.geo = geo;

    var cam = new THREE.PerspectiveCamera(34, 1, Math.max(0.01, r * 0.02), r * 60); this.cam = cam;
    this.theta = -0.6; this.tTheta = this.theta;
    this.phi = 1.02; this.tPhi = this.phi;
    this.radius = r * 3.4; this.tRadius = this.radius;
    this.minR = r * 1.4; this.maxR = r * 6.5;

    this._bindControls();
    this._resize();
    this._ro = new ResizeObserver(this._resize.bind(this)); this._ro.observe(host);
    this._start();
    requestAnimationFrame(function () { host.classList.add('gw-pv-ready'); });
  }

  Viewer.prototype._env = function (THREE, renderer, scene) {
    var c = document.createElement('canvas'); c.width = 16; c.height = 256;
    var g = c.getContext('2d'), grd = g.createLinearGradient(0, 0, 0, 256);
    grd.addColorStop(0.0, '#f2f5f9'); grd.addColorStop(0.5, '#d9e0e9'); grd.addColorStop(1.0, '#aeb9c6');
    g.fillStyle = grd; g.fillRect(0, 0, 16, 256);
    var tex = new THREE.CanvasTexture(c); tex.mapping = THREE.EquirectangularReflectionMapping; tex.colorSpace = THREE.SRGBColorSpace;
    var pm = new THREE.PMREMGenerator(renderer); this.envRT = pm.fromEquirectangular(tex);
    scene.environment = this.envRT.texture; tex.dispose(); pm.dispose();
  };

  Viewer.prototype._bindControls = function () {
    var el = this.renderer.domElement, self = this;
    var pts = new Map(), pinchD = 0;
    el.style.touchAction = 'none';
    function two() { var a = Array.from(pts.values()); return Math.hypot(a[0].x - a[1].x, a[0].y - a[1].y); }
    function down(e) {
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY }); self.idle = 0;
      if (pts.size === 1) self.dragging = true;
      else if (pts.size === 2) { self.dragging = false; pinchD = two(); }
      el.setPointerCapture && e.pointerId != null && el.setPointerCapture(e.pointerId);
    }
    function move(e) {
      var p = pts.get(e.pointerId); if (!p) return; self.idle = 0;
      if (pts.size >= 2) {
        p.x = e.clientX; p.y = e.clientY; var nd = two();
        if (pinchD > 0 && nd > 0) { self.tRadius *= pinchD / nd; self.tRadius = Math.max(self.minR, Math.min(self.maxR, self.tRadius)); }
        pinchD = nd;
      } else {
        self.tTheta -= (e.clientX - p.x) * 0.007; self.tPhi -= (e.clientY - p.y) * 0.007;
        self.tPhi = Math.max(0.15, Math.min(1.5, self.tPhi)); p.x = e.clientX; p.y = e.clientY;
      }
    }
    function up(e) { pts.delete(e.pointerId); if (pts.size < 2) pinchD = 0; self.dragging = pts.size >= 1; self.idle = 0; }
    el.addEventListener('pointerdown', down);
    window.addEventListener('pointermove', move, { passive: true });
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    el.addEventListener('wheel', function (e) { e.preventDefault(); self.tRadius *= (1 + (e.deltaY > 0 ? 0.09 : -0.09)); self.tRadius = Math.max(self.minR, Math.min(self.maxR, self.tRadius)); }, { passive: false });
    this._ctl = { move: move, up: up };
  };

  Viewer.prototype._resize = function () {
    var w = this.host.clientWidth || 480, h = this.host.clientHeight || 300;
    this.renderer.setSize(w, h, false); this.cam.aspect = w / h; this.cam.updateProjectionMatrix();
  };

  Viewer.prototype._start = function () {
    if (this.raf) return; var self = this;
    var loop = function (t) {
      self.raf = requestAnimationFrame(loop);
      var dt = self.lastT ? (t - self.lastT) / 16.67 : 1; self.lastT = t;
      if (!self.dragging) { self.idle += dt; if (self.idle > 40) self.tTheta += 0.0024 * dt; }
      self.theta += (self.tTheta - self.theta) * 0.12;
      self.phi += (self.tPhi - self.phi) * 0.12;
      self.radius += (self.tRadius - self.radius) * 0.12;
      var sp = Math.sin(self.phi);
      self.cam.position.set(self.radius * sp * Math.sin(self.theta), self.radius * Math.cos(self.phi), self.radius * sp * Math.cos(self.theta));
      self.cam.lookAt(0, 0, 0);
      self.renderer.render(self.scene, self.cam);
    };
    this.raf = requestAnimationFrame(loop);
  };

  Viewer.prototype.dispose = function () {
    if (this.raf) cancelAnimationFrame(this.raf);
    if (this._ro) this._ro.disconnect();
    window.removeEventListener('pointermove', this._ctl.move);
    window.removeEventListener('pointerup', this._ctl.up);
    window.removeEventListener('pointercancel', this._ctl.up);
    if (this.geo) this.geo.dispose(); if (this.mat) this.mat.dispose();
    if (this.envRT) this.envRT.dispose();
    if (this.renderer) { this.renderer.dispose(); if (this.renderer.domElement && this.renderer.domElement.parentNode) this.renderer.domElement.parentNode.removeChild(this.renderer.domElement); }
    this.host.classList.remove('gw-pv-ready');
  };

  /* ---------- public API ---------- */
  var GWPartViewer = {
    render: function (host, source) {
      if (!host) return Promise.reject(new Error('no host'));
      host.classList.add('gw-pv-loading'); host.classList.remove('gw-pv-error');
      return loadThree().then(function (THREE) {
        var p = source.arrayBuffer ? Promise.resolve(source.arrayBuffer)
          : fetch(source.url).then(function (r) { if (!r.ok) throw new Error('mesh ' + r.status); return r.arrayBuffer(); });
        return p.then(function (buf) {
          var geo = parseSTL(THREE, buf);
          if (!geo.getAttribute('position') || geo.getAttribute('position').count < 3) throw new Error('leeres Mesh');
          if (host.__pv) { try { host.__pv.dispose(); } catch (e) {} }
          host.innerHTML = '';
          host.__pv = new Viewer(THREE, host, geo);
          host.classList.remove('gw-pv-loading');
          return host.__pv;
        });
      }).catch(function (err) {
        host.classList.remove('gw-pv-loading'); host.classList.add('gw-pv-error');
        if (window.console) console.warn('GWPartViewer:', err && err.message);
        throw err;
      });
    },
    clear: function (host) {
      if (host && host.__pv) { try { host.__pv.dispose(); } catch (e) {} host.__pv = null; }
      if (host) { host.innerHTML = ''; host.classList.remove('gw-pv-ready', 'gw-pv-loading', 'gw-pv-error'); }
    }
  };
  window.GWPartViewer = GWPartViewer;
})();
